import { faArrowLeft, faArrowRight, faTimes } from "@fortawesome/pro-solid-svg-icons";
import cx from "classnames";
import memoize from "lodash-es/memoize";
import React from "react";

import { StrollerPrices } from "@/mk2/apps/strollers/components/StrollerPrices";
import { dummyPhoto } from "@/mk2/apps/strollers/constants";
import { Props } from "@/mk2/apps/strollers/containers/StrollersComparison/StrollersComparisonPage";
import { LISTED_FEATURE_NAMES } from "@/mk2/apps/strollers/containers/StrollersDetail/FeaturesList";
import { FEATURES_LIST } from "@/mk2/apps/strollers/features";
import { getProductIsNoLongerSoldLabel } from "@/mk2/apps/strollers/helpers";
import { strollersDetailUrl, strollersReviewsUrl, strollersUrl } from "@/mk2/apps/strollers/urls";
import { BreadcrumbSiteStructure } from "@/mk2/components/BreadcrumbSiteStructure";
import { Btn, BtnLayout, BtnType } from "@/mk2/components/Btn";
import { ColorVariants } from "@/mk2/components/ColorVariants";
import { ImgCropMode, ImgCropped } from "@/mk2/components/ImgCropped";
import { Link } from "@/mk2/components/Link";
import { StarsRating } from "@/mk2/components/StarsRating";
import { LoadingSwitch } from "@/mk2/containers/LoadingSwitch/LoadingSwitch";
import { url } from "@/mk2/helpers/urls";
import { interpolate } from "@/mk2/services/i18n";
import {
    COLORS,
    PRICE,
    RATING,
    STROLLERS_COMPARISON,
    STROLLERS_COMPARISON__SHOW_VARIOUS,
    STROLLERS_COMPARISON__SHOW_VARIOUS_OR_SAME,
    STROLLER_STROLLEROPEDIA,
    STROLLER_UNPREPARED_TAG,
    STROLLER_counter_REVIEWS,
    WIKI_REVIEWS,
} from "@/mk/autogenerated/translations/StrollersComparison.80fc96d3cac00f28c341c05309ab4c73";

import styles from "./StrollersComparison.mscss";

enum ComparisonRowLevel {
    UNDEFINED_ALL = 0, // all cells on the row are undefined, never show these rows
    SAME = 1, // all cells on the row are defined, these rows are optionally visible
    VARIOUS = 2, // all cells on the row are defined, various values, these rows are always visible
}
interface OwnState {
    showRowsOfComparisonLevel: ComparisonRowLevel;
}

export class StrollersComparison extends React.Component<Props, OwnState> {
    private static getBreadcrumbItems() {
        const breadcrumbLinks = [];
        breadcrumbLinks.push(
            <Link className={styles.BreadcrumbItem} key="strolleropedia" to={strollersUrl}>
                {STROLLER_STROLLEROPEDIA}
            </Link>,
        );
        breadcrumbLinks.push(<>{STROLLERS_COMPARISON}</>);
        return breadcrumbLinks;
    }

    public state: OwnState = {
        showRowsOfComparisonLevel: ComparisonRowLevel.VARIOUS,
    };

    private onStrollersComparisonToggle = memoize((id) => () => {
        this.props.onToggle(id, this.props.location);
    });

    public render() {
        const { strollers, loadingState } = this.props;

        return (
            <div className={styles.StrollersList}>
                <LoadingSwitch
                    loadingState={loadingState}
                    onLoad={this.onLoad}
                    onRenderSuccess={this.renderSuccess}
                    hasData={strollers && strollers.length > 0}
                />
            </div>
        );
    }

    private onStrollersComparisonMove = (id, direction: "left" | "right") => () => {
        const arr = this.props.strollers?.map((s) => s.id);
        const oldIndex = arr.indexOf(id);
        const newIndex = direction === "right" ? oldIndex + 1 : oldIndex - 1;
        if (newIndex >= 0 && newIndex < arr.length) {
            arr.splice(newIndex, 0, arr.splice(oldIndex, 1)[0]);
            this.props.onStoreOrdering(arr, this.props.location);
        }
    };

    private renderSuccess = () => {
        const { strollers, strollersPreviewPhotoMap, strollersPhotosMap } = this.props;

        if (!strollers.length) {
            return null;
        }

        const showActions = strollers.length > 1;
        const showColorVariants = false; // variants not implemented in StrollersComparison
        const showRowsOfComparisonLevel =
            strollers.length > 1 ? this.state.showRowsOfComparisonLevel : ComparisonRowLevel.SAME; // show all rows if there is one item

        return (
            <div className={styles.StrollersComparison}>
                <BreadcrumbSiteStructure
                    items={StrollersComparison.getBreadcrumbItems()}
                    className={styles.StrollersComparison__breadcrumb}
                />
                <div className={styles.StrollersComparison__outer}>
                    <div className={styles.StrollersComparison__inner}>
                        <table>
                            <tbody>
                                {showActions && (
                                    <tr>
                                        <th
                                            className={cx(
                                                styles.StrollersComparison__headerCell,
                                                styles.StrollersComparison__noBorderTop,
                                                styles.StrollersComparison__noBorderBottom,
                                            )}
                                        >
                                            &nbsp;
                                        </th>
                                        {strollers.map((stroller, key) => (
                                            <td
                                                className={cx(
                                                    styles.StrollersComparison__contentCell,
                                                    styles.StrollersComparison__noBorderTop,
                                                    styles.StrollersComparison__noBorderBottom,
                                                )}
                                                key={key}
                                            >
                                                <Btn
                                                    layout={BtnLayout.Icon}
                                                    icon={faArrowLeft}
                                                    onClick={this.onStrollersComparisonMove(
                                                        stroller.id,
                                                        "left",
                                                    )}
                                                    disabled={key === 0}
                                                />
                                                <Btn
                                                    layout={BtnLayout.Icon}
                                                    icon={faTimes}
                                                    className={styles.StrollersComparison__red}
                                                    onClick={this.onStrollersComparisonToggle(
                                                        stroller.id,
                                                    )}
                                                />
                                                <Btn
                                                    layout={BtnLayout.Icon}
                                                    icon={faArrowRight}
                                                    onClick={this.onStrollersComparisonMove(
                                                        stroller.id,
                                                        "right",
                                                    )}
                                                    disabled={key === strollers.length - 1}
                                                />
                                            </td>
                                        ))}
                                    </tr>
                                )}
                                <tr>
                                    <th
                                        className={cx(
                                            styles.StrollersComparison__headerCell,
                                            styles.StrollersComparison__noBorderTop,
                                            styles.StrollersComparison__noBorderBottom,
                                        )}
                                    >
                                        &nbsp;
                                    </th>
                                    {strollers.map((stroller, key) => {
                                        const strollerPhoto =
                                            stroller.id in strollersPreviewPhotoMap &&
                                            strollersPreviewPhotoMap[stroller.id] in
                                                strollersPhotosMap
                                                ? strollersPhotosMap[
                                                      strollersPreviewPhotoMap[stroller.id]
                                                  ]
                                                : dummyPhoto;
                                        return (
                                            <td
                                                className={cx(
                                                    styles.StrollersComparison__contentCell,
                                                    styles.StrollersComparison__noBorderTop,
                                                    styles.StrollersComparison__noBorderBottom,
                                                )}
                                                key={key}
                                            >
                                                <ImgCropped
                                                    width={100}
                                                    height={100}
                                                    mode={ImgCropMode.Embed}
                                                    imgWidth={strollerPhoto.originalWidth}
                                                    imgHeight={strollerPhoto.originalHeight}
                                                    imgUrl={strollerPhoto.url100x100}
                                                    alt={stroller.name}
                                                    className={
                                                        styles.StrollersComparison__contentCell__img
                                                    }
                                                />
                                            </td>
                                        );
                                    })}
                                </tr>
                                <tr>
                                    <th
                                        className={cx(
                                            styles.StrollersComparison__headerCell,
                                            styles.StrollersComparison__noBorderTop,
                                        )}
                                    >
                                        &nbsp;
                                    </th>
                                    {strollers.map((stroller, key) => {
                                        const productIsNoLongerSold =
                                            getProductIsNoLongerSoldLabel(stroller);
                                        return (
                                            <td
                                                className={cx(
                                                    styles.StrollersComparison__contentCell,
                                                    styles.StrollersComparison__noBorderTop,
                                                    styles.StrollersComparison__title,
                                                )}
                                                key={key}
                                            >
                                                <Link
                                                    to={url(strollersDetailUrl, {
                                                        slug: stroller.slug,
                                                    })}
                                                >
                                                    {stroller.name}
                                                </Link>
                                                {(!stroller.prepared || productIsNoLongerSold) && (
                                                    <div
                                                        className={styles.StrollersComparison__tags}
                                                    >
                                                        {!stroller.prepared && (
                                                            <span
                                                                className={
                                                                    styles.StrollersComparison__unpreparedTag
                                                                }
                                                            >
                                                                {STROLLER_UNPREPARED_TAG}
                                                            </span>
                                                        )}
                                                        {productIsNoLongerSold && (
                                                            <span
                                                                className={
                                                                    styles.StrollersComparison__productIsNoLongerSoldTag
                                                                }
                                                            >
                                                                {productIsNoLongerSold}
                                                            </span>
                                                        )}
                                                    </div>
                                                )}
                                            </td>
                                        );
                                    })}
                                </tr>
                                <tr>
                                    <th className={styles.StrollersComparison__headerCell}>
                                        {RATING}
                                    </th>
                                    {strollers.map((stroller, key) => (
                                        <td
                                            className={styles.StrollersComparison__contentCell}
                                            key={key}
                                        >
                                            <Link
                                                to={url(strollersReviewsUrl, {
                                                    slug: stroller.slug,
                                                })}
                                                className={styles.StrollersDetail__link}
                                            >
                                                <StarsRating
                                                    starsCount={stroller.userScore}
                                                    canShowHalf
                                                />
                                                <span
                                                    className={
                                                        styles["StrollersDetail__ratingBox--href"]
                                                    }
                                                >
                                                    {interpolate(STROLLER_counter_REVIEWS, {
                                                        counter: stroller.reviewsCount,
                                                        count: stroller.reviewsCount,
                                                    })}
                                                </span>
                                            </Link>
                                        </td>
                                    ))}
                                </tr>
                                <tr>
                                    <th className={styles.StrollersComparison__headerCell}>
                                        {PRICE}
                                    </th>
                                    {strollers.map((stroller, key) => (
                                        <td
                                            className={styles.StrollersComparison__contentCell}
                                            key={key}
                                        >
                                            <StrollerPrices
                                                className={styles.StrollersComparison__prices}
                                                priceUsedMin={stroller.priceUsedMin}
                                                priceUsedMax={stroller.priceUsedMax}
                                                minPricedEshopProductPrice={
                                                    stroller.minPricedEshopProduct
                                                }
                                                maxPricedEshopProductPrice={
                                                    stroller.maxPricedEshopProduct
                                                }
                                            />
                                        </td>
                                    ))}
                                </tr>
                                {showColorVariants && (
                                    <tr>
                                        <th className={styles.StrollersComparison__headerCell}>
                                            {COLORS}
                                        </th>
                                        {strollers.map((stroller, key) => (
                                            <td
                                                className={styles.StrollersComparison__contentCell}
                                                key={key}
                                            >
                                                <ColorVariants colors={null /*tbd*/} maxShown={5} />
                                            </td>
                                        ))}
                                    </tr>
                                )}
                                {LISTED_FEATURE_NAMES.map((name) => {
                                    const feature = FEATURES_LIST[name];
                                    if (!feature) {
                                        throw new Error(`Unknown feature: ${name}`);
                                    }
                                    return feature;
                                }).map((feature, fkey) => {
                                    const ValueComponent = feature.Component;
                                    let featureComparisonLevel: ComparisonRowLevel =
                                        ComparisonRowLevel.UNDEFINED_ALL;
                                    let lastFeatureValue = null;
                                    for (const stroller of strollers) {
                                        let val = stroller.features[feature.featureName];
                                        if (val === undefined) {
                                            val = stroller.variantAttributes[feature.featureName];
                                        }

                                        if (val) {
                                            featureComparisonLevel = ComparisonRowLevel.SAME;
                                            if (lastFeatureValue === null) {
                                                lastFeatureValue = JSON.stringify(val);
                                            } else {
                                                const currFeatureValue = JSON.stringify(val);
                                                if (currFeatureValue !== lastFeatureValue) {
                                                    featureComparisonLevel =
                                                        ComparisonRowLevel.VARIOUS;
                                                    break;
                                                }
                                            }
                                        }
                                    }

                                    if (featureComparisonLevel < showRowsOfComparisonLevel) {
                                        return null;
                                    }

                                    return (
                                        <tr key={`feature-${fkey}`}>
                                            <th className={styles.StrollersComparison__headerCell}>
                                                {feature.getLabelMessage()}
                                            </th>
                                            {strollers.map((stroller, skey) => {
                                                const values = feature.featureName.startsWith(
                                                    "variant_",
                                                )
                                                    ? stroller.variantAttributes
                                                    : stroller.features;

                                                return (
                                                    <td
                                                        className={
                                                            styles.StrollersComparison__contentCell
                                                        }
                                                        key={`stroller-${skey}`}
                                                    >
                                                        {values[feature.featureName] ? (
                                                            <ValueComponent
                                                                className={
                                                                    styles.StrollersComparison__contentCell__value
                                                                }
                                                                features={values}
                                                                feature={feature}
                                                            />
                                                        ) : (
                                                            <div
                                                                className={
                                                                    styles.StrollersComparison__contentCell__value
                                                                }
                                                            >
                                                                -
                                                            </div>
                                                        )}
                                                    </td>
                                                );
                                            })}
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                </div>
                {strollers.length > 1 && (
                    <div className={styles.StrollersComparison__toggleShow}>
                        <Btn
                            label={
                                this.state.showRowsOfComparisonLevel === ComparisonRowLevel.VARIOUS
                                    ? STROLLERS_COMPARISON__SHOW_VARIOUS_OR_SAME
                                    : STROLLERS_COMPARISON__SHOW_VARIOUS
                            }
                            onClick={this.onToggle}
                            type={BtnType.Link}
                        />
                    </div>
                )}
            </div>
        );
    };

    private onLoad = () => {
        this.props.onLoad();
    };

    private onToggle = () => {
        if (this.state.showRowsOfComparisonLevel === ComparisonRowLevel.VARIOUS) {
            this.setState({ showRowsOfComparisonLevel: ComparisonRowLevel.SAME });
        } else {
            this.setState({ showRowsOfComparisonLevel: ComparisonRowLevel.VARIOUS });
        }
    };
}
